/**
 * Text banks and combinators for the Strange archetype generator.
 *
 * Grounded in non-standard physics, alien biology/substrates, and unfamiliar
 * but coherent cultures. All banks provide >= 12 variants per slot.
 */

import type { InterestId, StrengthId, AmbitionId, LifePathId } from "../types";

export interface IdentityArchetype {
  id: string;
  name: string;
  substrate: "biological" | "silicon" | "acoustic" | "field";
  bodyDesc: string;
  sensoryMode: string;
  socialUnit: string;
  dailyCadence: string;
}

export const STRANGE_IDENTITIES: readonly IdentityArchetype[] = [
  {
    id: "tidal_ferrier",
    name: "Tidal-Lock Ferrier",
    substrate: "biological",
    bodyDesc: "a low-slung, multi-jointed quadrupedal form shielded by thermal silicate plates",
    sensoryMode: "electrostatic whiskers that detect barometric shifts and thermal pressure gradients",
    socialUnit: "a nomadic convoy of seasonal ridge-dwellers linked by carbon tethers",
    dailyCadence: "migrating continuously along the twilight terminator between boiling dayside plains and frozen nightside chasms",
  },
  {
    id: "manifold_choir",
    name: "Many-Bodied Acoustic Choir",
    substrate: "acoustic",
    bodyDesc: "a distributed triad of semi-autonomous resonance shells connected through harmonic sub-vocalizing",
    sensoryMode: "lithic acoustic ears that map subterranean crystal faults through continuous infrasound clicking",
    socialUnit: "an interlocking guild of harmonic tuners sharing a single composite memory bank",
    dailyCadence: "sounding massive obsidian bell-spires at dawn to balance civic acoustic navigation networks",
  },
  {
    id: "external_memory_keeper",
    name: "Crystalline Memory-Exchanger",
    substrate: "silicon",
    bodyDesc: "a bipedal silicate frame with external mica storage spindles resting along the dorsal crest",
    sensoryMode: "polarized ultraviolet vision capable of reading encoded diffraction lines etched into living stone",
    socialUnit: "a scholastic clade of archival curators who swap experiential crystals at dusk",
    dailyCadence: "transcribing the day's tactile sensations onto micro-faceted quartz ribbons before sleep wipes biological recall",
  },
  {
    id: "hibernal_strider",
    name: "Deep-Time Hibernal Sleeper",
    substrate: "biological",
    bodyDesc: "a tall, slender form reinforced with slow-metabolizing cartilaginous struts and cryo-dampening dermal fluid",
    sensoryMode: "infrared photoreceptors tuned to the faint thermal decay of deep geothermal vents",
    socialUnit: "an ancient generational succession circle where members wake in alternating rotational centuries",
    dailyCadence: "living active waking periods of sixty days every solar decade to maintain slow-cycle planetary gardens",
  },
  {
    id: "tether_weaver",
    name: "Gravitational Flume Diver",
    substrate: "biological",
    bodyDesc: "a lean, elongated silhouette with prehensile wing-flaps and micro-hooked talons suited for shear-wind traversal",
    sensoryMode: "inner-ear statolith organs capable of sensing minute micro-gravity eddies and tidal vectors",
    socialUnit: "a cliff-hanging flotilla commune suspended within the atmospheric inversion zone",
    dailyCadence: "free-falling through thousands of meters of dense troposphere before catching thermal updrafts on woven mica sails",
  },
  {
    id: "plasma_symbiont",
    name: "Atmospheric Plasma Symbiont",
    substrate: "field",
    bodyDesc: "a translucent, bioluminescent membrane sustained by ionized electrical currents",
    sensoryMode: "electromagnetic field resonance that feels thoughts as oscillating voltage ripples across open gas bands",
    socialUnit: "a communal atmospheric vortex that converges during solar noon to exchange navigational theorems",
    dailyCadence: "drifting through high-altitude magnetic squalls, filtering trace methane and ionizing particulate clouds",
  },
  {
    id: "spore_mycelial",
    name: "Sub-Crust Spore Harvester",
    substrate: "biological",
    bodyDesc: "a dense, compact frame with moisture-trapping chitin and sensory root-tendrils extending from each limb",
    sensoryMode: "chemosensory taste-vibrations received through the ground, feeling footsteps leagues away",
    socialUnit: "a subterranean burrow-hearth cooperative tied to ancestral mycelial root systems",
    dailyCadence: "harvesting bioluminescent spore filaments from basalt chambers where cavern roofs chime under tectonic pressure",
  },
  {
    id: "refractive_prism_walker",
    name: "Refractive Horizon Scout",
    substrate: "silicon",
    bodyDesc: "a faceted crystalline chassis that bends ambient light into dazzling chromatic halos",
    sensoryMode: "wide-spectrum chromatic refraction, observing reality through overlapping parabolas and polarized rings",
    socialUnit: "a guild of solitude-bound horizon cartographers who signal one another via focused solar beams",
    dailyCadence: "standing at the edge of curved planetary rifts to plot the shifting optical paths of binary suns",
  },
  {
    id: "argon_tide_diver",
    name: "Heavy-Argon Chemist",
    substrate: "biological",
    bodyDesc: "a thick-ribbed amphibious form with pneumatic gills built to inhale dense noble gas atmospheres",
    sensoryMode: "acoustic side-line organs that register compression waves moving through heavy fluid-like air",
    socialUnit: "a coastal terrace clan harvesting chemical precipitates from mineralized amber seas",
    dailyCadence: "diving into thick valley floors where air settles like warm water, tending farms of metallic kelp",
  },
  {
    id: "toroidal_loop_runner",
    name: "Toroidal Wayfinder",
    substrate: "biological",
    bodyDesc: "a spring-limbed biped with gyroscopic internal balance and pressure-resistant tendon arrays",
    sensoryMode: "spherical horizon vision that instinctively calculates non-Euclidean loop closures and upside-down vistas",
    socialUnit: "a messenger confederacy running continuous circuits across the inner circumference of the world-loop",
    dailyCadence: "sprinting across arched stone bridges where looking overhead reveals the upside-down settlements of neighboring loops",
  },
  {
    id: "stellar_filament_shepherd",
    name: "Vacuum Filament Weaver",
    substrate: "field",
    bodyDesc: "a coherent lace of superconducting carbon fibers guided by faint electrostatic charges",
    sensoryMode: "gravitational wave detection that hears the slow orbital waltz of distant moons as musical drones",
    socialUnit: "an orbital anchor cluster coordinating planetary counterweights across empty space",
    dailyCadence: "threading ultra-thin diamond-composite tethers between drifting asteroids to stabilize regional gravity wells",
  },
  {
    id: "mnemonic_vaporist",
    name: "Sub-Planar Geothermal Tuner",
    substrate: "acoustic",
    bodyDesc: "a flexible, segmented mantle composed of vulcanized volcanic resin and acoustic reed chambers",
    sensoryMode: "thermal acoustic intuition, translating the hiss of boiling steam vents into precise geological maps",
    socialUnit: "a council of vent-hearth elders stewarding pressurized clean-water flumes for three hanging towns",
    dailyCadence: "adjusting pressure relief valves along obsidian cliffs while chanting harmonic counter-frequencies to prevent tremors",
  },
];

export const STRANGE_TITLES_PARTS = {
  prefixes: [
    "Harmonizer", "Cartographer", "Tide-Caster", "Warden", "Weaver",
    "Thermal Pilot", "Lithic Sculptor", "Chronologer", "Scout", "Keeper",
    "Resonance Tuner", "Sub-Planar Guide", "Acoustic Siphon", "Horizon Balancer",
    "Pressure Weaver", "Flume Diver",
  ],
  modifiers: [
    "of Variable Gravities", "of Non-Euclidean Ridges", "of Heavy Argon Seas",
    "of the Toroidal Crests", "of Singing Basalt", "of Inconstant Dawn",
    "of Sub-Surface Fungi", "of the Resonant Chasm", "of Amber Horizons",
    "of Hanging Domiciles", "of Crystalline Vents", "of Polarized Halos",
    "of Inverted Horizons", "of Metric Shear", "of the Twilight Flume",
    "of Mnemonic Spores",
  ],
};

export const STRANGE_TAGLINE_PARTS = {
  settings: [
    "Where gravity shifts with barometric tides and stepping wrong means floating for hours,",
    "Navigating an alien landscape where light bends in observable parabolas across open plains,",
    "Living under an amber sky where sound travels twice as fast through super-dense air,",
    "Adapted to a world whose geometry folds back onto itself like an endless crystalline labyrinth,",
    "Harvesting bioluminescent minerals from crystalline ravines where stone rings like brass bells,",
    "Where everyday survival depends on calculating the subtle micro-currents of fluctuating gravity,",
    "In a high-pressure chasm where air is thick as warm tea and speech is sung through reeds,",
    "Perched on floating aerostats suspended above boundless geothermal steam geysers,",
    "Under binary amber suns whose cross-shadows turn daily movement into a geometric puzzle,",
    "Along the singing ridges of a toroidal world where you look straight up to see neighboring towns,",
    "Within subterranean basalt vaults where thought is stored on Faceted quartz spires,",
    "On the razor edge between boiling perpetual noon and zero-Kelvin glacial dusk,",
  ],
  stances: [
    "you find delicate poetry and warm domestic comfort within radical physical laws.",
    "a life attuned to sensory spectra biological eyes could never perceive.",
    "every morning begins with calculating the curvature of your own horizon.",
    "trading human familiarity for the fierce, breathtaking logic of an alien home.",
    "proving that consciousness and tenderness flourish under the most unearthly skies.",
    "where communal solidarity is the only tether preventing a drift into the abyss.",
    "mastering an everyday rhythm that turns terrifying cosmic extremes into quiet craft.",
    "anchored by chosen bonds that outshine the strange geometries of the firmament.",
  ],
};

export const OCCUPATION_PARTS: Record<
  InterestId,
  { domains: readonly string[]; methods: readonly string[] }
> = {
  science: {
    domains: [
      "Gravitational Gradient Cartographer",
      "Fluctuating Constant Metrologist",
      "High-Density Atmospheric Chemist",
      "Spatial Topology Analyst",
      "Lithic Harmonic Seismologist",
      "Non-Euclidean Geometer",
    ],
    methods: [
      "measuring barometric tidal shifts with diamond pendulums",
      "mapping curved light corridors through faceted quartz apertures",
      "tracking subterranean acoustic resonances through singing basalt",
      "calibrating noble gas density sensors along deep rift valleys",
    ],
  },
  art: {
    domains: [
      "Bioluminescent Spore Choreographer",
      "Harmonic Resonance Sculptor",
      "Folded-Space Tapestry Weaver",
      "Refractive Amber Polisher",
      "Vapor-Pressure Geoglyphist",
      "Chromatic Halo Painter",
    ],
    methods: [
      "weaving tensile fabrics that hum in sympathy with atmospheric wind shear",
      "polishing translucent mica plates to split binary sunlight into living frescoes",
      "arranging glowing fungal mycelia across hanging cliff faces",
      "sculpting resonant obsidian flutes that turn gale winds into melodic hymns",
    ],
  },
  music: {
    domains: [
      "Thermal Pressure Flutist",
      "Lithic Chime Resonator",
      "High-Argon Acoustic Tuner",
      "Sub-Surface Drum Synchronizer",
      "Atmospheric Reed Harmonist",
      "Infrasound Canyon Singer",
    ],
    methods: [
      "tuning communal bell spires to counter destructive seismic harmonics",
      "performing duets with geothermal vent geysers across the canyon rim",
      "translating barometric fluctuations into acoustic civic navigation tones",
      "sounding brass resonance horns from high basalt perches during storm fronts",
    ],
  },
  nature: {
    domains: [
      "Silicon-Based Spore Harvester",
      "Atmospheric Jelly Skimmer",
      "Heavy-Matter Lichen Forager",
      "Thermal Vent Biome Cultivator",
      "High-Altitude Aerostat Botanist",
      "Lithic Coral Tender",
    ],
    methods: [
      "nurturing drought-tolerant lichen along sheer volcanic chimneys",
      "shepherding migratory gas-siphon organisms across thermal inversion boundaries",
      "gathering crystalline nectar from pressure-hardened chasm flowers",
      "monitoring nutrient flumes in subterranean moisture nurseries",
    ],
  },
  technology: {
    domains: [
      "Tethered-Glider Framework Smith",
      "Variable-Mass Anchor Engineer",
      "Atmospheric Siphon Machinist",
      "Crystal-Aperture Optics Grinder",
      "Pneumatic Cable Winch Fabricator",
      "Sub-Planar Valve Regulator",
    ],
    methods: [
      "forging carbon-mesh cables capable of withstanding violent atmospheric shear",
      "hand-machining counterweight hydraulics from dense meteoric alloys",
      "grinding diamond-coated lens arrays for non-Euclidean optical telegraphs",
      "building automated steam-pressure governors along active volcanic vents",
    ],
  },
  people: {
    domains: [
      "Ridge-Habitat Clan Mediator",
      "Migratory Wind-Route Guide",
      "Acoustic Dialect Interpreter",
      "Thermal Sanctuary Quartermaster",
      "Hanging-Terrace Civic Arbiter",
      "Terminator Flotilla Convener",
    ],
    methods: [
      "reconciling water-condensation quotas among neighboring cliff settlements",
      "translating tactile resonance pulses between disparate silicate clades",
      "organizing collective shelter rotations during seasonal radiation surges",
      "maintaining peaceful navigation accords along crowded thermal wind gates",
    ],
  },
  stories: {
    domains: [
      "Oral Keeper of the Changing Horizons",
      "Cartographer of Shifting Ridge Paths",
      "Chronicler of Great Pressure Inversions",
      "Weaver of Sky-Current Epics",
      "Archivist of Etched Mica Tablets",
      "Chanter of Lineage Faults",
    ],
    methods: [
      "inscribing oral migration chronicles onto polished obsidian cliff faces",
      "preserving generational survival logs through rhythmic infrasound memory chants",
      "recording tectonic boundary shifts on flexible quartz ribbons",
      "narrating the ancient crossing of the great atmospheric gulf to young fledglings",
    ],
  },
  adventure: {
    domains: [
      "Deep-Chasm Glider Scout",
      "High-Toroid Wind Sailor",
      "Thermal Boundary Explorer",
      "Floating Archway Pioneer",
      "Twilight Terminator Wayfarer",
      "Sub-Crust Chasm Breaker",
    ],
    methods: [
      "piloting ultralight mica wings through unmapped high-altitude pressure corridors",
      "scaling vertical basalt escarpments using pneumatic suction pitons",
      "surveying uncharted thermal plumes deep within the atmospheric abyss",
      "crossing treacherous sulfur salt flats on articulated gliding skiffs",
    ],
  },
};

export const LIFE_PATH_MANNER: Record<
  LifePathId,
  { modifier: string; style: string; bias: string }
> = {
  rooted: {
    modifier: "Anchor-Bound",
    style: "grounded in the deep stewardship of a single hanging chasm or ancestral terrace",
    bias: "prioritizing generational resilience and unbroken local custom over distant horizons",
  },
  scholar: {
    modifier: "Archival",
    style: "devoting waking cycles to meticulous observation, tactile measurement, and recording anomalous constants",
    bias: "relying on repeatable empirical records rather than unverified clan myth",
  },
  maker: {
    modifier: "Artisanal",
    style: "shaping survival through hands-on fabrication with local minerals, cables, and heat valves",
    bias: "trusting only what has been tested under real atmospheric pressure at the workbench",
  },
  voyager: {
    modifier: "Itinerant",
    style: "perpetually riding high-altitude thermal corridors between distant hanging settlements",
    bias: "embracing the freedom and solitary chill of open atmospheric chasms",
  },
  guide: {
    modifier: "Communal",
    style: "mediating between rival terrace clans and mentoring fledglings in the arts of aerodynamic survival",
    bias: "measuring personal worth by the collective safety of the migratory flock",
  },
  founder: {
    modifier: "Pioneering",
    style: "establishing brand-new suspension habitats and forging untested trade routes through violent shear zones",
    bias: "daring to anchor where traditional councils insisted life could never hold",
  },
};

export const ORIGIN_PARTS = {
  settings: [
    "Born upon the tiered hanging cliffs of the Great Rift where atmospheric pressure is thick as warm water,",
    "Raised within a toroidal world-ridge where looking straight up means seeing the upside-down terraces of neighboring settlements,",
    "Growing up along heavy-argon coastlines where amber waves break against crystalline basalt pillars,",
    "In a high-gravity plateau settlement where bone densities double and movement is deliberate as a ceremonial dance,",
    "Under a sky illuminated by a close binary pair of amber suns whose cross-shadows turn daily life into a shifting geometric puzzle,",
    "Raised within floating aerostats anchored to deep geothermal vents thousands of meters above an uninhabitable crust,",
    "Along the edges of a fractured gravitational fault where loose shale drifts upward during solar noon,",
    "In a world where electromagnetic fluctuations create permanent, dancing sheets of emerald aurora across the lower troposphere,",
    "Nurtured inside subterranean basalt chambers where thermal vents supply continuous warm moisture and acoustic solace,",
    "Formed along the twilight terminator band, straddling perpetual scorching midday on one side and frozen glacial night on the other,",
    "Raised in an orbital tether colony where centrifugal spinning simulates the faint pull of home,",
    "Born within a crystalline sinkhole where mineral-rich condensation drips onto singing stone bells with rhythmic precision,",
  ],
  formativeBeats: [
    "{name} learned to calculate micro-currents of atmospheric shear before taking a single unsupported leap into the open void.",
    "{name} was apprenticed to the blind stone-tuners, discovering that hairline structural fractures in the cliff face sang in minor third intervals.",
    "early childhood was spent tracking the graceful migratory arcs of radiant airborne siphon-beasts through dense amber clouds.",
    "{name} mastered the art of swimming through dense nitrogen fog using webbed arms and counterweight lead belts.",
    "watching elder scouts navigate treacherous crosswinds instilled an unshakeable reverence for meticulous aerodynamic planning.",
    "{name} spent hours watching light refract through natural quartz prisms, realizing ordinary vision captured only a fraction of the world's real curvature.",
    "the family hearth was sustained by harvesting thermal sap from deep basalt vents, teaching {name} the economy of calculated effort.",
    "{name} developed an uncanny ability to taste ozone spikes hours before a barometric pressure inversion tore through the gorge.",
    "growing up in close quarters with five allied clans taught {name} that quiet diplomacy is the strongest suspension cable.",
    "{name} learned to read memory facets on crystalline mica ribbons, absorbing three centuries of clan lore before adulthood.",
    "early labor in the hanging lichen orchards forged exceptional grip strength and an absolute immunity to vertigo.",
    "{name} learned to navigate entirely by electrostatic skin-tingle during the seasonal blackout dust-storms.",
  ],
  outcomes: [
    "This forged an instinct for finding grace and stability where outsiders would see only terrifying vertigo.",
    "The experience left an indelible imprint: survival here is not about defying physical laws, but dancing in effortless harmony with them.",
    "From that point onward, the peculiar physics of this cosmos felt not like an obstacle, but like an affectionate, demanding teacher.",
    "This background instilled a profound patience that outlasts even the most violent planetary storm seasons.",
    "It gave {name} a reputation among neighboring terraces as someone whose calm never wavers, even when the suspension anchors groan.",
    "Living under such constants meant {name} never took breath, balance, or communal trust for granted.",
    "It established an intuitive mathematical mind capable of calculating curved horizons without paper or instruments.",
    "This upbringing anchored an unshakeable loyalty to the collective survival of the cliff-dwelling clans.",
  ],
};

export const TURNING_POINT_PARTS = {
  catalysts: [
    "When a catastrophic atmospheric pressure inversion threatened to shear the primary suspension cables of the central terrace,",
    "When traditional clan elders declared the resonant crystal caverns of the lower chasm to be forbidden untouchable zones,",
    "During the year of the Great Flume Collapse, when freshwater condensation conduits froze solid across thirty settlements,",
    "When an unexpected gravitational surge caused loose shale and heavy mining equipment to drift uncontrollably toward the sky,",
    "Confronted with an ultimatum from an aggressive lowland merchant cartel seeking a monopoly over thermal updraft routes,",
    "When an unmapped geothermal eruption opened a pristine, glowing fissure through the ancestral obsidian wall,",
    "During the Great Atmospheric Migration, when blinding electromagnetic dust-fog disabled all acoustic navigation chimes,",
    "When rival cliff-dwelling factions stood armed upon their tethered gliders, ready to fight over dwindling water flumes,",
    "Offered an elevated, sheltered seat on the High Terrace Council in exchange for abandoning field exploration,",
    "When a sudden barometric collapse left three hundred migratory fledglings stranded on an unshielded basalt spire,",
    "When discovering an ancient, hand-carved wind-flute that revealed previous civilizations had solved the very crisis facing the clans,",
    "At the threshold of an unprecedented solitary circumnavigation across the world's continuous outer loop,",
  ],
  actions: [
    "{name} strapped on custom glide-wings and dove into the violent vortex, securing the retaining pins bare-handed just as the wind turned.",
    "{name} stepped between the opposing glider lines, using acoustic resonance horns to expose the hidden geological data that guaranteed water for both sides.",
    "{name} spent three uninterrupted seasons cataloging the subterranean harmonic frequencies, proving the singing crystals were an early warning system for tectonic rifts.",
    "{name} engineered the first low-gravity hydraulic dampening cradles using salvaged carbon mesh and local mineral resin.",
    "{name} climbed the highest basalt spire during a gale-force magnetic storm, sounding manual warning tones for forty straight hours.",
    "{name} declined the council seat, choosing instead to lead the emergency restoration crew into the scalding lower vents.",
    "{name} devised an unconventional aerodynamic route that utilized non-Euclidean horizon curvature to bypass the storm completely.",
    "{name} shared the secret maps of the thermal corridors openly with all twelve clans, breaking the cartel's monopoly forever.",
    "{name} established a permanent low-gravity botanical sanctuary on the outer rim of the world-loop, ending seasonal famine.",
    "{name} led a daring expedition into the deep glowing mist where no clan member had ventured for three centuries, retrieving wild progenitor lichen cultures.",
    "{name} built a high-altitude thermal glider to verify firsthand how space curved seamlessly back upon itself, putting an end to centuries of dogmatic dispute.",
    "{name} adopted and trained three orphaned fledglings from a historically hostile settlement, creating an unbreakable kinship bridge.",
  ],
  consequences: [
    "The act permanently altered regional history, earning {name} lifelong grassroots trust and establishing an enduring safety standard.",
    "Though the effort cost two broken ribs and permanent thermal scarring along the forearms, it preserved five hundred lives and united the valley.",
    "This decisive moment proved that understanding the world's radical physics yields far more protection than fearful superstition.",
    "It transformed {name} from an ordinary tradesperson into a legendary beacon of ingenuity across the entire hanging archipelago.",
    "The resulting peace accord has held for an entire generation, proving that courage paired with clarity can untangle deep-seated enmities.",
    "It cemented a personal philosophy that has never wavered: when the universe shifts its weight, you lean into the curve.",
    "The innovation sparked a technological renaissance across the rift, inspiring young makers to rethink the limits of their environment.",
    "It proved that the oddest aspects of their world's cosmology were not curses to be endured, but gifts waiting to be understood.",
  ],
};

export const TODAY_PARTS = {
  routines: [
    "Today, {name} steps out onto the launch terrace of their hanging domicile, adjusting glide-harness straps while watching violet clouds swirl through the canyon depths below.",
    "Life has settled into an exhilarating, rhythmic communion with the alien winds: daily surveys of tethered anchors, shared meals of steamed lichen cakes, and the silent joy of flight.",
    "Working at an open-air drafting ledge carved directly into sheer obsidian, {name} uses fine diamond chisels to mark newly discovered thermal corridors on flexible mica sheets.",
    "Evenings are spent around the communal vent-hearths, drinking warm spiced sap while listening to elder singers translate deep tectonic rumbles into folklore.",
    "Neighbors know {name} as the person who can predict a gravitational fluctuation three hours before instruments twitch, simply by the taste of ozone on the breeze.",
    "Inside a woven hammock suspended across the chasm, {name} falls asleep to the low, comforting rumble of planetary steam vents and the soft glow of drifting phosphors.",
    "Each dawn begins with a ceremonial sounding of the valley's lead chime, verifying that the air density is optimal for young gliders to make their morning flights.",
    "Surrounded by worktables laden with pneumatic valves, tensile cables, and polished quartz lenses, {name} moves with effortless, zero-waste precision.",
    "From the high observation ledge, {name} monitors the slow migration of luminous atmospheric siphon-beasts, recording their shifting routes in leather-bound logs.",
    "Resting after a long traverse of the ridge, {name} cleans mineral dust from specialized goggles while sharing dried sweet moss with traveling wayfarers.",
    "At sunset, {name} tends the rooftop condensation flumes, ensuring that clean, mineral-balanced water flows steadily into the communal cisterns below.",
    "Perched on a narrow basalt outcrop where looking upward reveals the upside-down gardens of the opposite loop, {name} sketches the geometric symmetry of home.",
  ],
  perspectives: [
    "The hands are toughened by rough hemp cables and mineral resin, but {name} moves with the fluid grace of an entity perfectly adapted to their environment.",
    "Looking out across the bizarre, awe-inspiring curves of this world's horizon, {name} feels no desire for simpler physics; this strange reality is home, rich and complete.",
    "There is quiet satisfaction in knowing that every suspension bolt holding this town above the abyss was inspected with honest, uncompromising care.",
    "Though outsiders might find this life terrifying, {name} experiences it as a tapestry of breathtaking beauty, profound solidarity, and daily wonder.",
    "Every breath of high-density air is a reminder that conscious life does not require gentle constants—it requires only courage, craft, and care.",
    "With a calm pulse and a steady eye on the shifting pressure dials, {name} knows that tomorrow's winds will bring fresh puzzles, and welcomes them.",
    "The community sleeps soundly because people like {name} keep vigil on the rim, translating the wild music of the universe into human safety.",
    "Sitting quietly as the twin amber suns sink behind the jagged basalt towers, {name} smiles at the thought of alternate worlds, entirely content with this one.",
  ],
};

export const STRANGE_CHOICES_BANK = [
  {
    age: "Cycle 16",
    choice: "Chose to master the treacherous high-altitude glider winds instead of remaining in the secure lowland burrows.",
    roadNotTaken: "Accepting a quiet subterranean mushroom-farming apprenticeship.",
    consequence: "Suffered two broken ribs in early crosswinds, but gained uninhibited mastery of the sky-gorges.",
  },
  {
    age: "Cycle 21",
    choice: "Refused to surrender the secret map of thermal currents to the monopolistic merchant cartel.",
    roadNotTaken: "Selling the coordinates for a chest of rare polished amber jewels.",
    consequence: "Preserved open navigation for all migratory clans, cementing lifelong grassroots loyalty.",
  },
  {
    age: "Cycle 28",
    choice: "Volunteered to anchor the emergency stabilizer cable during the catastrophic Solstice Storm.",
    roadNotTaken: "Taking shelter in the reinforced interior granite vaults.",
    consequence: "Saved the entire residential terrace from tearing loose and drifting into the abyss.",
  },
  {
    age: "Cycle 18",
    choice: "Refused to participate in the traditional territorial raid across the eastern cloud-rift.",
    roadNotTaken: "Joining the clan warriors to pillage freshwater condensing basins.",
    consequence: "Endured temporary ostracism, but later brokered the permanent peace treaty between both valleys.",
  },
  {
    age: "Cycle 25",
    choice: "Constructed the first low-gravity botanical conservatory on the outer rim of the world-loop.",
    roadNotTaken: "Devoting labor solely to standard mineral extraction.",
    consequence: "Introduced fifteen drought-resistant lichen species that ended seasonal famine.",
  },
  {
    age: "Cycle 33",
    choice: "Adopted and raised three orphaned fledglings from the rival cliff-dwelling tribe.",
    roadNotTaken: "Turning them over to the indifferent clan welfare barracks.",
    consequence: "Created a bridge of kinship that permanently united two historically hostile settlements.",
  },
  {
    age: "Cycle 15",
    choice: "Dove into the heavy-argon tide pool to rescue a fallen atmospheric survey instrument.",
    roadNotTaken: "Watching the expensive bronze sensor sink into the dense chemical sludge.",
    consequence: "Suffered mild chemical burns to the forearms, but recovered crucial climate records.",
  },
  {
    age: "Cycle 24",
    choice: "Chose the nomadic life of a wind-corridor surveyor over a permanent seat on the High Hearth.",
    roadNotTaken: "Enjoying the warm prestige and bountiful rations of municipal governance.",
    consequence: "Mapped two thousand leagues of previously uncharted atmospheric highways.",
  },
  {
    age: "Cycle 35",
    choice: "Repaired the geothermal steam regulator with bare hands during a sudden sub-surface rupture.",
    roadNotTaken: "Evacuating the sector and allowing the steam manifold to detonate.",
    consequence: "Sustained permanent heat scarring, but prevented the destruction of the lower residential tier.",
  },
  {
    age: "Cycle 17",
    choice: "Apprenticed with the acoustic stone-tuners instead of following the family mining trade.",
    roadNotTaken: "Wielding heavy pneumatic drills in the iron-heavy basalt quarries.",
    consequence: "Developed an auditory sensitivity capable of detecting hairline structural fractures in the cliffs.",
  },
  {
    age: "Cycle 26",
    choice: "Disclosed the discovery of the pristine crystal geode caverns to the entire public assembly.",
    roadNotTaken: "Keeping the rich mineral cache secret for private enrichment.",
    consequence: "Transformed the caverns into a shared sanctuary and scientific research station.",
  },
  {
    age: "Cycle 34",
    choice: "Stepped down from the Chief Explorer post to train the next generation of youth scouts.",
    roadNotTaken: "Attempting one final, vanity-driven solo crossing of the outer vortex.",
    consequence: "Ensured the clan's survival skills remained sharp and widely distributed.",
  },
  {
    age: "Cycle 19",
    choice: "Built an experimental carbon-fiber sail to test non-Euclidean glide ratios along the chasm rim.",
    roadNotTaken: "Sticking to heavy, certified canvas gliders sanctioned by the elders.",
    consequence: "Discovered an updraft corridor that cut inter-terrace travel time by two-thirds.",
  },
  {
    age: "Cycle 27",
    choice: "Sheltered twenty migrant siphon-harvesters when the guild revoked their seasonal atmospheric permits.",
    roadNotTaken: "Reporting their presence to the guild bailiffs to secure a personal bonus.",
    consequence: "Faced official censure, but earned a network of lifelong scouts across thirty rifts.",
  },
  {
    age: "Cycle 36",
    choice: "Hand-carved thirty monumental acoustic wind flutes into the sheer obsidian face of the central rift.",
    roadNotTaken: "Retiring into quiet domestic trade inside the sheltered valley.",
    consequence: "Turned deadly seasonal gales into an awe-inspiring civic warning chorus.",
  },
];

export const STRANGE_SKILLS_BANK: Record<InterestId, string[]> = {
  science: [
    "Barometric tidal forecasting",
    "Non-Euclidean horizon geodesics",
    "Infrasound seismology",
    "Noble gas atmospheric distillation",
    "Variable-G trajectory plotting",
  ],
  art: [
    "Tensile resonance weaving",
    "Refractive mica etching",
    "Bioluminescent spore choreography",
    "Obsidian wind-flute carving",
    "Chromatic polarized drafting",
  ],
  music: [
    "Acoustic canyon echolocation",
    "Seismic chime harmonization",
    "High-argon vocal projection",
    "Sub-crust percussion rhythmics",
    "Infrasound pulse signaling",
  ],
  nature: [
    "Extreme-pressure botany",
    "Thermal vent lichen husbandry",
    "Aerostat fauna tracking",
    "Silicate root grafting",
    "Atmospheric siphon foraging",
  ],
  technology: [
    "Carbon-mesh rigging and tensioning",
    "Hydraulic counterweight calibration",
    "Pneumatic valve machining",
    "Diamond-aperture grinding",
    "Zero-gravity tether splicing",
  ],
  people: [
    "Cross-clade dialect translation",
    "Inter-terrace water quota mediation",
    "High-altitude crisis triage",
    "Suspension safety inspection",
    "Flotilla council diplomacy",
  ],
  stories: [
    "Oral tectonic chronology",
    "Mica-facet lore transcription",
    "Pressure-inversion mythography",
    "Rhythmic navigation mnemonic chants",
    "Ancestral migration charting",
  ],
  adventure: [
    "Thermal-shear glide piloting",
    "Vertical obsidian free-climbing",
    "Atmospheric inversion diving",
    "Sulfur salt-flat skiff sailing",
    "Sub-crust chasm reconnaissance",
  ],
};

export const STRANGE_MOTIVATIONS_BANK: Record<AmbitionId, string[]> = {
  understand: [
    "Deciphering why the local gravitational field oscillates with barometric pressure, seeking fundamental cosmological coherence.",
    "Mapping every unrecorded non-Euclidean contour of the planetary horizon so future generations never fly blind.",
    "Proving that the bizarre physics of this universe can be mastered through patient observation rather than fatalistic fear.",
  ],
  build: [
    "Constructing suspension systems and counterweight anchors that withstand the fiercest atmospheric shear for centuries.",
    "Engineering a network of permanent clean-water condensation flumes across all twelve hanging settlements.",
    "Leaving behind physical infrastructure that allows cliff-dwelling clans to flourish without fear of falling.",
  ],
  help: [
    "Ensuring no fledgling or elder is ever left behind when sudden pressure inversions force emergency migrations.",
    "Distributing drought-resistant lichen cultures unconditionally across historically divided clans.",
    "Providing reliable acoustic storm warnings so that isolated hunting parties find their way home safely.",
  ],
  lead: [
    "Uniting the scattered ridge clans into an enduring confederation capable of managing shared atmospheric corridors.",
    "Dismantling merchant monopolies that restrict access to freshwater flumes and vital thermal updraft passes.",
    "Modeling a style of calm, evidence-based leadership when catastrophic tectonic tremors shake communal resolve.",
  ],
  create: [
    "Transforming terrifying atmospheric gales into breathtaking harmonic symphonies through hand-carved cliff flutes.",
    "Weaving fabrics that respond dynamically to shifting air pressure, turning daily garments into living art.",
    "Etching the epic survival chronicles of the rift onto luminous obsidian ledgers for deep-time memory.",
  ],
  wander: [
    "Breaching the forbidden lower pressure ceilings to discover what thrives in the glowing geothermal depths.",
    "Circumnavigating the outer rim of the world-loop on custom glider wings to witness virgin horizons.",
    "Living at the razor edge between light and shadow on the twilight terminator, where no settlement dares anchor.",
  ],
};

export const STRANGE_RELATIONSHIPS_BANK = [
  {
    role: "Senior Flume Engineer & Clan Elder",
    whoGen: "alien",
    bondTemplate:
      "{who} taught {name} the precise art of tensioning suspension cables along the sheer chasm wall. Their bond is rooted in decades of shared close calls and mutual reverence for honest craft.",
  },
  {
    role: "Rival Wind-Sailor & Cartographer",
    whoGen: "alien",
    bondTemplate:
      "A fierce yet honorable competitor who constantly races {name} to map new thermal updraft passes. While they dispute navigational routes in public council, neither would hesitate to catch the other's lifeline.",
  },
  {
    role: "Kin-Sibling & Lichen Biologist",
    whoGen: "alien",
    bondTemplate:
      "Raised together on the mid-level terraces, {who} tends the hanging fungal gardens that sustain the family domicile. Their late-night conversations over spiced sap anchor {name} through the stormiest cycles.",
  },
  {
    role: "Acoustic Stone-Tuner & Mentor",
    whoGen: "alien",
    bondTemplate:
      "{who} first introduced {name} to the singing basalt caves, training their ears to hear seismic shifts hours before instruments register. Their mentorship remains a quiet moral compass.",
  },
  {
    role: "Adopted Fledgling & Apprentice",
    whoGen: "alien",
    bondTemplate:
      "Rescued during a violent barometric shear, {who} is now learning the intricacies of glide navigation under {name}'s patient eye. Mentoring them has given {name} a deeper stake in the valley's future.",
  },
  {
    role: "Non-Human Aerostat Symbiont Companion",
    whoGen: "alien",
    bondTemplate:
      "A bioluminescent siphon-beast named {who} that nests along {name}'s launch terrace. The creature's electrostatic sensitivity serves as an unfailing barometric warning system during long flights.",
  },
  {
    role: "Terrace Quartermaster & Confidant",
    whoGen: "alien",
    bondTemplate:
      "{who} manages the communal provisions for the hanging district, frequently trading rare dried moss for {name}'s updated wind charts. Their practical pragmatism balances {name}'s daring curiosity.",
  },
  {
    role: "Exiled Ridge Scout & Long-Distance Ally",
    whoGen: "alien",
    bondTemplate:
      "Living along the outer perimeter where few dare venture, {who} exchanges coded light-signals with {name} at dawn, sharing early warnings about shifting vortex paths.",
  },
];

export const STRANGE_ACHIEVEMENTS_BANK = [
  "Engineered the multi-tiered counterweight damper system that saved the lower residential terrace during the catastrophic Solstice Storm.",
  "Charted seventy leagues of uncharted thermal wind corridors through the Great Rift, reducing travel time between hanging clans by two-thirds.",
  "Successfully negotiated the Historic Water Flume Concordat, resolving a twenty-year territorial feud between cliff-dwelling factions.",
  "Discovered that subterranean singing crystals act as natural piezoelectric seismic sensors, creating the valley's first reliable earthquake warning network.",
  "Cultivated a drought-resistant hybridized lichen strain that eliminated seasonal protein shortages across thirty hanging settlements.",
  "Completed a solo non-stop glider traverse of the world-loop's inner circumference, surviving two separate violent atmospheric shear events.",
  "Hand-carved a series of monumental acoustic flutes along the gorge that turned deadly seasonal gales into an awe-inspiring warning symphony.",
  "Rescued twenty-four stranded miners from a collapsed basalt pocket by constructing an improvised pneumatic winch within six hours.",
];

export const STRANGE_FAILURES_BANK = [
  "Misjudged a violent thermal shear during an early test flight, destroying an experimental glider prototype and losing three irreplaceable barometric logbooks to the chasm.",
  "Hesitated during the flood of Cycle 27, arriving too late to prevent an ancestral seed vault from washing into the sulfur flats.",
  "Bluntly rejected an administrative compromise with the regional council, provoking a temporary commercial blockade that strained the terrace's fuel reserves.",
  "Failed to notice a hairline fracture in the primary anchor cable of the northern cargo crane, resulting in the loss of a valuable season's harvest.",
  "Over-extended a solo scouting run into the forbidden lower pressure zone, suffering severe nitrogen narcosis and requiring a high-risk rescue by peers.",
  "Trusted a disingenuous merchant envoy with confidential flume coordinates, allowing water speculators to temporarily inflate communal rations.",
];

export const STRANGE_DILEMMAS_BANK = [
  "Balancing the urgent need to expand residential terraces into the mineral-rich lower chasm against the known risk of catastrophic geothermal vent ruptures.",
  "Deciding whether to reveal the coordinates of a pristine freshwater geode cavern to the public assembly, knowing that open access could rapidly deplete the fragile reservoir.",
  "Grappling with the duty to train emerging youth scouts in high-risk glider winds while knowing that even a single sudden atmospheric shear can prove fatal.",
  "Choosing between preserving ancient clan customs that forbid touching resonant crystals and deploying piezoelectric mining to secure clean energy for freezing winters.",
  "Weighing personal longing to embark on an uncharted solo expedition across the outer vortex against the heavy civic responsibility of stewarding the hanging district's anchors.",
  "Navigating the tension between open-sourcing thermal flight maps to all migratory clans and keeping sensitive routes shielded from exploitative trading cartels.",
];
